import { useCallback, useEffect } from "react";
import { toast } from "sonner";

import { useNetworkStatus } from "@/hooks";

/**
 * OfflineInteractionBlocker Component
 *
 * Prevents user interactions (clicks on links, buttons, form submissions)
 * when the app is offline, showing a helpful toast instead of letting
 * the action fail with an error.
 *
 * This component uses a document-level event listener approach:
 * - Intercepts click events in the capture phase (before they reach targets)
 * - Checks if the click target is an interactive element
 * - Prevents the event and shows a toast when offline
 * - Allows scrolling and non-interactive clicks
 *
 * Why this approach:
 * - Catches all navigation (including TanStack Router's <Link> components)
 * - Prevents dynamic module loading errors when routes can't be fetched
 * - Prevents mutation attempts that would fail
 * - Doesn't require modifying every component
 * - Keeps content visible and scrollable
 */
export function OfflineInteractionBlocker() {
  const { status } = useNetworkStatus();

  // Only block during "offline" state (browser has no network)
  // During "reconnecting" state, browser is online so navigation works
  // (JS chunks can load, only Convex queries are delayed)
  const isOffline = status === "offline";

  // Debounce toast to prevent spam on rapid clicks
  const showOfflineToast = useCallback(() => {
    // Use toast.error with an ID to prevent duplicates
    toast.error("You're offline", {
      id: "offline-interaction-blocked",
      description: "This action requires an internet connection",
      duration: 3000,
    });
  }, []);

  useEffect(() => {
    if (!isOffline) {
      return;
    }

    /**
     * Intercept clicks on interactive elements when offline.
     * Uses capture phase to intercept before the event reaches the target.
     */
    function handleClick(event: MouseEvent) {
      const target = event.target as HTMLElement;

      // Find the closest interactive element (link, button, or clickable)
      const interactiveElement = target.closest(
        'a, button, [role="button"], [role="link"], [role="tab"], [role="menuitem"], ' +
          'input[type="submit"], input[type="button"], [data-clickable], [onclick]'
      );

      if (interactiveElement) {
        // Check if it's a link that would navigate
        const isLink =
          interactiveElement.tagName === "A" || interactiveElement.getAttribute("role") === "link";

        // Check if it's a button or interactive control
        const isButton =
          interactiveElement.tagName === "BUTTON" ||
          interactiveElement.getAttribute("role") === "button" ||
          interactiveElement.getAttribute("role") === "tab" ||
          interactiveElement.getAttribute("role") === "menuitem";

        // Check if it's a form submit
        const isSubmit =
          interactiveElement instanceof HTMLInputElement &&
          (interactiveElement.type === "submit" || interactiveElement.type === "button");

        if (isLink || isButton || isSubmit) {
          event.preventDefault();
          event.stopPropagation();
          showOfflineToast();
        }
      }
    }

    /**
     * Intercept form submissions when offline.
     */
    function handleSubmit(event: SubmitEvent) {
      event.preventDefault();
      event.stopPropagation();
      showOfflineToast();
    }

    /**
     * Intercept keyboard navigation (Enter on links/buttons).
     */
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      const target = event.target as HTMLElement;
      const interactiveElement = target.closest(
        'a, button, [role="button"], [role="link"], [role="tab"]'
      );

      if (interactiveElement) {
        event.preventDefault();
        event.stopPropagation();
        showOfflineToast();
      }
    }

    // Add listeners in capture phase to intercept before target handlers
    document.addEventListener("click", handleClick, { capture: true });
    document.addEventListener("submit", handleSubmit, { capture: true });
    document.addEventListener("keydown", handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener("click", handleClick, { capture: true });
      document.removeEventListener("submit", handleSubmit, { capture: true });
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [isOffline, showOfflineToast]);

  // This component doesn't render anything - it's purely behavioral
  return null;
}
